import type {
  FormSectionState,
  StateField,
  StateFieldType,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { roundToPlaces } from '@navstation-portal/shared/util/math';
import { getFieldFromState, isNullOrNaN, isStateField } from '../../offshore-vm-builder-helpers';

const WIND_FORCE_THRESHOLDS: number[] = [0.3, 1.5, 3.3, 5.5, 7.9, 10.7, 13.8, 17.1, 20.7, 24.4, 28.4, 32.6];

const OFFSET_FOR_CURRENT_TRANSFORM = 'scale(1,-1) translate(0,-54)';

export type OffshorePositionAndWeatherVm = ReturnType<typeof buildOffshorePositionAndWeatherVm>;

export interface OffshorePositionAndWeatherFormVmSections {
  [key: string]: (() => FormSectionState | undefined) | undefined;
  positionAndLogistics: () => FormSectionState;
}

export function buildOffshorePositionAndWeatherVm(store: OffshorePositionAndWeatherFormVmSections) {
  const state = {
    positionAndLogistics: store.positionAndLogistics?.(),
  };
  const isTrueWindReporting = () => {
    const trueWindReporting = getFieldFromState(state, 'wind', 'trueWindReporting');

    return Boolean((trueWindReporting as StateField<StateFieldType>)?.value);
  };

  const calculateTrueWindForce = () => {
    const trueWindSpeedField = getFieldFromState(state, 'speedAndDistance', 'courseOverGround');
    const trueWindSpeed = (trueWindSpeedField as StateField<StateFieldType>)?.value as number;

    if (isNullOrNaN(trueWindSpeed)) return;

    let trueWindForce = 0;
    for (const threshold of WIND_FORCE_THRESHOLDS) {
      if (trueWindSpeed < threshold) {
        break;
      }
      trueWindForce++;
    }

    return trueWindForce;
  };

  const isSpeedLogMalfunction = () => {
    const speedLogMalfunction = getFieldFromState(state, 'speedAndDistance', 'speedLogMalfunction');

    return Boolean((speedLogMalfunction as StateField<StateFieldType>)?.value);
  };

  const calculateTrueWindDirection = () => {
    const trueWindDirectionField = getFieldFromState(state, 'wind', 'trueWindDirection');
    const trueWindDirection = (trueWindDirectionField as StateField<StateFieldType>)?.value as number;
    const courseOverGroundField = getFieldFromState(state, 'speedAndDistance', 'courseOverGround');
    const courseOverGround = (courseOverGroundField as StateField<StateFieldType>)?.value as number;
    if (isNullOrNaN(trueWindDirection) || isNullOrNaN(courseOverGround)) return;

    const trueWindEffect = roundToPlaces((trueWindDirection + courseOverGround) % 360, 2);
    if (isNaN(trueWindEffect)) return;

    return trueWindEffect;
  };

  const getValueFromState = (section: string, field: string) => {
    const fieldFromState = getFieldFromState(state, section, field);
    if (!fieldFromState || !isStateField(fieldFromState)) return undefined;
    const value = fieldFromState.value;
    if (isNullOrNaN(Number(value))) return undefined;
    return Number(value);
  };

  const calculateApparentCurrent = () => {
    const failedResult = {
      apparentCurrentSpeed: undefined,
      apparentCurrentDirection: undefined,
    };

    const observedSpeed = getValueFromState('speedAndDistance', 'observedSpeed');
    if (observedSpeed === undefined) return failedResult;

    const trueCurrentSpeed = getValueFromState('currentsAndSeaWater', 'trueCurrentSpeed');
    if (trueCurrentSpeed === undefined) return failedResult;

    const currentDirectionTrueNorth = getValueFromState('currentsAndSeaWater', 'currentDirectionTrueNorth');
    if (currentDirectionTrueNorth === undefined) return failedResult;

    const courseOverGround = getValueFromState('speedAndDistance', 'courseOverGround');
    if (courseOverGround === undefined) return failedResult;

    let apparentCurrentDirection = currentDirectionTrueNorth - courseOverGround;
    if (apparentCurrentDirection < 0) apparentCurrentDirection += 360;
    const apparentCurrentSpeed = trueCurrentSpeed * Math.cos((apparentCurrentDirection * Math.PI) / 180);

    return {
      apparentCurrentSpeed: roundToPlaces(apparentCurrentSpeed, 2),
      apparentCurrentDirection: roundToPlaces(apparentCurrentDirection, 2),
    };
  };

  const getDirection = (value = 0, isCurrentDir: boolean) => {
    if (!isCurArrowVisible) value += 180;
    const centerX = 280;
    const centerY = 180;
    const height = 130;
    const shiftDown = centerY + height * Math.cos((value * Math.PI) / 180);
    const shiftLeft = centerX - (height * Math.cos(((90 - value) * Math.PI) / 180)) / 1.5;

    const baseTransform = `translate(${shiftLeft}, ${shiftDown}) rotate(${value})`;
    return isCurrentDir ? `${baseTransform} ${OFFSET_FOR_CURRENT_TRANSFORM}` : baseTransform;
  };

  const apparentWaveDirection = getValueFromState('currentsAndSeaWater', 'apparentWaveDirection');
  const apparentWindDirection = getValueFromState('wind', 'apparentWindDirection');

  const { apparentCurrentSpeed, apparentCurrentDirection } = calculateApparentCurrent();
  const isWindArrowVisible = apparentWindDirection != undefined ? true : false;
  const isWaveArrowVisible = apparentWaveDirection != undefined ? true : false;
  const isCurArrowVisible = apparentCurrentDirection != undefined ? true : false;
  return {
    isTrueWindReporting: isTrueWindReporting(),
    trueWindForce: calculateTrueWindForce(),
    trueWindDirection: calculateTrueWindDirection(),
    isSpeedLogMalfunction: isSpeedLogMalfunction(),
    apparentCurrentDirection,
    apparentCurrentSpeed,
    isCurArrowVisible,
    isWaveArrowVisible,
    isWindArrowVisible,
    windDirectionTransform: getDirection(apparentWindDirection, false),
    waveDirectionTransform: getDirection(apparentWaveDirection, false),
    currentDirectionTransform: getDirection(apparentCurrentDirection, true),
  };
}
